import styled from '@emotion/styled';
import type { ReactNode } from 'react';
import Icon from 'storybookConfig/stories/molecules/Icon';

const COLLAPSE_MS = 280;
const STAGGER_MS = 30;

/*
 * The height animates through grid rows (0fr ↔ 1fr), so no height is measured. Items fade and
 * drop in one after another as it opens, and leave together as it closes. Closed content stays
 * mounted but is hidden from pointer, keyboard and screen readers once the close has played.
 */
const CollapseOuter = styled.div<{ open: boolean }>`
  display: grid;
  grid-template-rows: ${({ open }) => (open ? '1fr' : '0fr')};
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
  transition:
    grid-template-rows ${COLLAPSE_MS}ms ${({ theme }) => theme.motion.easeOut},
    visibility 0s linear ${({ open }) => (open ? '0s' : `${COLLAPSE_MS}ms`)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CollapseInner = styled.div<{ open: boolean }>`
  min-height: 0;
  overflow: hidden;

  /* Doubled class: Menu.Link opens with all: unset, which would reset these at equal weight. */
  && > * > * {
    opacity: ${({ open }) => (open ? 1 : 0)};
    transform: translateY(${({ open }) => (open ? 0 : '-4px')});
    transition:
      opacity ${COLLAPSE_MS}ms ${({ theme }) => theme.motion.easeOut},
      transform ${COLLAPSE_MS}ms ${({ theme }) => theme.motion.easeOut},
      background-color ${({ theme }) => theme.motion.hover} ${({ theme }) => theme.motion.easeOut},
      color ${({ theme }) => theme.motion.hover} ${({ theme }) => theme.motion.easeOut};
  }

  /*
   * The stagger counts every item, links and buttons alike, so it needs :nth-child, which Emotion
   * flags as unsafe for server rendering. The app renders on the client only; its documented
   * comment marks the selector as intended (without it, dev logs a console.error per section).
   */
  ${({ open }) =>
    open &&
    Array.from(
      { length: 10 },
      (_, i) =>
        `&& > * > :nth-child(${i + 1}) /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */ { transition-delay: ${i * STAGGER_MS}ms; }`
    ).join('\n')}

  @media (prefers-reduced-motion: reduce) {
    && > * > * {
      transition: none;
    }
  }
`;

export interface CollapseProps {
  /** Whether the content shows. */
  open: boolean;
  /** Lets the toggle point at it with `aria-controls`. */
  id?: string;
  children: ReactNode;
}

/**
 * A section's content, opening and closing smoothly (the sidebar's sections). The toggle that drives
 * it should carry `aria-expanded={open}` and `aria-controls={id}`.
 */
const Collapse = ({ open, id, children }: CollapseProps) => (
  <CollapseOuter id={id} open={open} aria-hidden={!open}>
    <CollapseInner open={open}>{children}</CollapseInner>
  </CollapseOuter>
);

const ChevronWrap = styled.span<{ open: boolean }>`
  display: inline-flex;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  transition: transform ${COLLAPSE_MS}ms ${({ theme }) => theme.motion.easeOut};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The toggle's chevron: it turns rather than swapping icons. */
export const CollapseChevron = ({ open }: { open: boolean }) => (
  <ChevronWrap open={open} aria-hidden>
    <Icon name="chevron-down" />
  </ChevronWrap>
);

export default Collapse;
